import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Observable, forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { EnvironmentService } from "./environment.service";
import {
  GiteaMigrationRequest,
  GiteaMigrationStatus,
  GiteaRepository,
  GiteaRepositoryDetail,
} from "../models/gitea";

@Injectable({
  providedIn: "root",
})
export class GiteaService {
  private baseUrl: string;

  constructor(private http: HttpClient, private env: EnvironmentService) {
    this.baseUrl =
      this.env.envVariables.httpProtocol + this.env.envVariables.webHost;
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders()
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
  }

  activeRepo: GiteaRepository | null = null;

  private selectedRepo = new BehaviorSubject<GiteaRepository | null>(null);
  public SelectedRepo = this.selectedRepo.asObservable();

  updateSelectedRepo(repo: GiteaRepository) {
    if (repo && this.selectedRepo.value != repo) {
      this.selectedRepo.next(repo);
    }
    setTimeout(() => {
      //this.SendPodAction.next(null);
    }, 1000);
  }

  getSelectedRepo() {
    return this.selectedRepo.value;
  }

  /**
   * Get all Gitea repositories
   */
  getGiteaReposAll(): Observable<GiteaRepository[]> {
    const url = `${this.baseUrl}Gitea/GetGiteaReposAll`;
    return this.http.get<GiteaRepository[]>(url, {
      headers: this.getHeaders(),
    });
  }

  /**
   * Get registered users in Gitea
   */
  getGiteaRegisteredUsers(): Observable<any[]> {
    const url = `${this.baseUrl}Gitea/GetGiteaRegisteredUsers`;
    return this.http.get<any[]>(url, { headers: this.getHeaders() });
  }

  /**
   * Get Gitea user information
   */
  getGiteaUserInfo(): Observable<any> {
    const url = `${this.baseUrl}Gitea/GetGiteaUserInfo`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  /**
   * Create Gitea user accounts
   */
  createGiteaUserAccount(userList: string[]): Observable<any> {
    const url = `${this.baseUrl}Gitea/CreateGiteaUserAccount`;
    return this.http.post(url, userList, { headers: this.getHeaders() });
  }

  /**
   * Get repository details including branches, commits, contributors, etc.
   */
  getRepositoryDetail(
    owner: string,
    repoName: string
  ): Observable<GiteaRepositoryDetail> {
    const url = `${this.baseUrl}Gitea/GetRepositoryDetail/${owner}/${repoName}`;
    return this.http.get<GiteaRepositoryDetail>(url, {
      headers: this.getHeaders(),
    });
  }

  /**
   * Get repository collaborators
   */
  getRepoCollaborators(owner: string, repoName: string): Observable<any[]> {
    const url = `${this.baseUrl}Gitea/GetRepoCollaborators/${owner}/${repoName}`;
    return this.http.get<any[]>(url, { headers: this.getHeaders() });
  }

  /**
   * Add collaborators to a repository
   */
  addCollaboratorToRepo(
    owner: string,
    repoName: string,
    collaborators: string[]
  ): Observable<any> {
    const url = `${this.baseUrl}Gitea/AddCollaboratorToRepo/${owner}/${repoName}`;
    const headers = this.getHeaders().set("RepoName", repoName);
    return this.http.post(url, collaborators, { headers: headers });
  }

  /**
   * Get repository branches
   */
  getRepoBranches(owner: string, repoName: string): Observable<any[]> {
    const url = `${this.baseUrl}Gitea/GetRepoBranches/${owner}/${repoName}`;
    return this.http.get<any[]>(url, { headers: this.getHeaders() });
  }

  /**
   * Get repository commits
   */
  getRepoCommits(
    owner: string,
    repoName: string,
    branch?: string
  ): Observable<any[]> {
    let url = `${this.baseUrl}Gitea/GetRepoCommits/${owner}/${repoName}`;
    if (branch) {
      url += `?branch=${branch}`;
    }
    return this.http.get<any[]>(url, { headers: this.getHeaders() });
  }

  /**
   * Get repository contributors
   */
  getRepoContributors(owner: string, repoName: string): Observable<any[]> {
    const url = `${this.baseUrl}Gitea/GetRepoContributors/${owner}/${repoName}`;
    return this.http.get<any[]>(url, { headers: this.getHeaders() });
  }

  /**
   * Get repository pull requests
   */
  getRepoPullRequests(
    owner: string,
    repoName: string,
    state?: string
  ): Observable<any[]> {
    let url = `${this.baseUrl}Gitea/GetRepoPullRequests/${owner}/${repoName}`;
    if (state) {
      url += `?state=${state}`;
    }
    return this.http.get<any[]>(url, { headers: this.getHeaders() });
  }

  /**
   * Create a new repository
   */
  createRepository(repoData: any): Observable<any> {
    const url = `${this.baseUrl}Gitea/CreateRepository`;
    return this.http.post(url, repoData, { headers: this.getHeaders() });
  }

  /**
   * Delete a repository
   */
  deleteRepository(owner: string, repoName: string): Observable<any> {
    const url = `${this.baseUrl}Gitea/DeleteRepository/${owner}/${repoName}`;
    return this.http.delete(url, { headers: this.getHeaders() });
  }

  /**
   * Fork a repository
   */
  forkRepository(
    owner: string,
    repoName: string,
    organization?: string
  ): Observable<any> {
    const url = `${this.baseUrl}Gitea/ForkRepository/${owner}/${repoName}`;
    const body = organization ? { organization } : {};
    return this.http.post(url, body, { headers: this.getHeaders() });
  }

  /**
   * Get repository issues
   */
  getRepoIssues(
    owner: string,
    repoName: string,
    state?: string
  ): Observable<any[]> {
    let url = `${this.baseUrl}Gitea/GetRepoIssues/${owner}/${repoName}`;
    if (state) {
      url += `?state=${state}`;
    }
    return this.http.get<any[]>(url, { headers: this.getHeaders() });
  }

  /**
   * Get repository releases
   */
  getRepoReleases(owner: string, repoName: string): Observable<any[]> {
    const url = `${this.baseUrl}Gitea/GetRepoReleases/${owner}/${repoName}`;
    return this.http.get<any[]>(url, { headers: this.getHeaders() });
  }

  /**
   * Get repository tags
   */
  getRepoTags(owner: string, repoName: string): Observable<any[]> {
    const url = `${this.baseUrl}Gitea/GetRepoTags/${owner}/${repoName}`;
    return this.http.get<any[]>(url, { headers: this.getHeaders() });
  }

  /**
   * Get repository file content
   */
  getFileContent(
    owner: string,
    repoName: string,
    filePath: string,
    ref?: string
  ): Observable<any> {
    let url = `${this.baseUrl}Gitea/GetFileContent/${owner}/${repoName}`;
    const headers = this.getHeaders().set(
      "FilePath",
      encodeURIComponent(filePath)
    );
    if (ref) {
      headers.set("Ref", ref);
    }
    return this.http.get(url, { headers: headers });
  }

  /**
   * Search repositories
   */
  searchRepositories(query: string, limit?: number): Observable<any> {
    let url = `${this.baseUrl}Gitea/SearchRepositories?q=${encodeURIComponent(
      query
    )}`;
    if (limit) {
      url += `&limit=${limit}`;
    }
    return this.http.get(url, { headers: this.getHeaders() });
  }

  /**
   * Get organization repositories
   */
  getOrgRepositories(orgName: string): Observable<GiteaRepository[]> {
    const url = `${this.baseUrl}Gitea/GetOrgRepositories/${orgName}`;
    return this.http.get<GiteaRepository[]>(url, {
      headers: this.getHeaders(),
    });
  }

  /**
   * Get user repositories
   */
  getUserRepositories(username: string): Observable<GiteaRepository[]> {
    const url = `${this.baseUrl}Gitea/GetUserRepositories/${username}`;
    return this.http.get<GiteaRepository[]>(url, {
      headers: this.getHeaders(),
    });
  }

  migrateRepository(
    migrationRequest: GiteaMigrationRequest
  ): Observable<GiteaMigrationStatus> {
    const url = `${this.baseUrl}Gitea/MigrateRepository`;
    return this.http.post<GiteaMigrationStatus>(url, migrationRequest, {
      headers: this.getHeaders(),
    });
  }
  migrateFromHuggingFace(
    huggingFaceUrl: string,
    repoName: string,
    repoOwner: string,
    isPrivate: boolean = false,
    description?: string,
    hfUsername?: string, // HuggingFace username
    hfAccessToken?: string // HuggingFace access token
  ): Observable<GiteaMigrationStatus> {
    const migrationRequest: GiteaMigrationRequest = {
      clone_addr: huggingFaceUrl,
      repo_name: repoName,
      repo_owner: repoOwner,
      service: "git",
      description:
        description || `Migrated from HuggingFace: ${huggingFaceUrl}`,
      private: isPrivate,
      mirror: false,
      wiki: true,
      milestones: true,
      labels: true,
      issues: false,
      pull_requests: false,
      releases: true,
      lfs: true,
      lfs_endpoint: "",
      // Add HuggingFace authentication if provided
      auth_username: hfUsername || "",
      auth_password: hfAccessToken || "", // HF access token goes in password field
      auth_token: "", // Alternative auth method (can use this instead of username/password)
    };

    return this.migrateRepository(migrationRequest);
  }
  /**
   * Migrate from HuggingFace using access token (recommended for private repos)
   */
  migrateFromHuggingFaceWithToken(
    huggingFaceUrl: string,
    repoName: string,
    repoOwner: string,
    hfAccessToken: string,
    isPrivate: boolean = false,
    description?: string
  ): Observable<GiteaMigrationStatus> {
    const migrationRequest: GiteaMigrationRequest = {
      clone_addr: huggingFaceUrl,
      repo_name: repoName,
      repo_owner: repoOwner,
      service: "git",
      description:
        description || `Migrated from HuggingFace: ${huggingFaceUrl}`,
      private: isPrivate,
      mirror: false,
      wiki: true,
      releases: true,
      lfs: true,
      // Use token-based authentication (preferred method)
      auth_token: hfAccessToken,
    };

    return this.migrateRepository(migrationRequest);
  }
  batchMigrateRepositories(
    migrations: GiteaMigrationRequest[]
  ): Observable<GiteaMigrationStatus[]> {
    const migrationObservables = migrations.map((migration) =>
      this.migrateRepository(migration).pipe(
        catchError((error) =>
          of({
            success: false,
            message: `Failed to migrate ${migration.repo_name}`,
            repository_url: null,
            error: error.message || "Unknown error",
          } as GiteaMigrationStatus)
        )
      )
    );

    return forkJoin(migrationObservables);
  }
}
